import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot, Trophy, CheckCircle2, ArrowRight, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AssistantsPage() {
  const [assistants, setAssistants] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/assistants").then(({ data }) => setAssistants(data || [])).catch(() => {});
  }, []);

  const tryExample = (id, prompt) => {
    if (user) nav(`/chat/${id}?prompt=${encodeURIComponent(prompt)}`);
    else nav("/signup");
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 md:px-10" data-testid="assistants-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Meet the team</p>
      <h1 className="mt-3 max-w-3xl font-display text-4xl font-medium tracking-tighter sm:text-5xl">
        15 AI specialists. <span className="text-accent">One mission: your success.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">
        Each assistant is trained for one job and does it brilliantly — together they cover everything from your CV to your brand.
      </p>

      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
        {assistants.map((a) => (
          <div key={a.id} className="flex h-full flex-col rounded-2xl border border-border bg-card p-6" data-testid={`assistants-card-${a.id}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Bot className="h-5 w-5" /></span>
              <div>
                <h2 className="font-display text-lg">{a.name}</h2>
                <p className="text-xs text-muted-foreground">{a.purpose}</p>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-xl border border-accent/30 bg-accent/5 p-3">
              <Trophy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
              <p className="text-xs"><span className="text-muted-foreground">Big outcome: </span><span className="font-medium">{a.outcome}</span></p>
            </div>

            <ul className="mt-4 space-y-1.5">
              {(a.top_tasks || []).map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {t}
                </li>
              ))}
            </ul>

            <div className="mt-4 flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Example prompts</p>
              <div className="mt-2 space-y-1.5">
                {(a.examples || []).map((ex, i) => (
                  <button key={i} onClick={() => tryExample(a.id, ex)}
                    className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 text-left text-xs transition-colors hover:border-primary/50"
                    data-testid={`assistants-example-${a.id}-${i}`}>
                    <MessageSquarePlus className="h-3 w-3 shrink-0 text-primary" /> {ex}
                  </button>
                ))}
              </div>
            </div>

            <Button size="sm" className="mt-4" onClick={() => (user ? nav(`/chat/${a.id}`) : nav("/signup"))} data-testid={`assistants-start-${a.id}`}>
              {user ? `Chat with ${a.name}` : "Start free"} <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {!user && (
        <div className="mt-12 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-8 text-center">
          <h2 className="font-display text-2xl">All 15 assistants. Free to start.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Plus your personal roadmap, AI coach and achievements.</p>
          <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="assistants-signup-cta">
            <Link to="/signup">Create my free account <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      )}
    </div>
  );
}
